/**
 * Connection utilities for handling network errors and Cloudflare outages
 */
import { supabase } from '../supabase';

export interface ConnectionTestResult {
  success: boolean;
  error?: string;
  isCloudflareDown?: boolean;
  isNetworkError?: boolean;
}

/**
 * Test Supabase connection with retry logic
 */
export const testSupabaseConnection = async (retries: number = 2): Promise<ConnectionTestResult> => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Simple test query
      const { error } = await supabase
        .from('CourseLevels')
        .select('id', { count: 'exact', head: true })
        .limit(1);
      
      if (error) {
        // Check for Cloudflare 503
        if (error.message?.includes('503') || error.message?.includes('Service Unavailable') || 
            (error as any).status === 503 || (error as any).code === '503') {
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          return { 
            success: false, 
            isCloudflareDown: true,
            error: 'Cloudflare service is temporarily unavailable. Please try again later.' 
          };
        }
        
        // Network errors
        if (error.message?.includes('fetch') || error.message?.includes('Network') || 
            error.message?.includes('Failed to fetch')) {
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            continue;
          }
          return { 
            success: false, 
            isNetworkError: true,
            error: 'Network error: Cannot connect to server.' 
          };
        }
        
        return { success: false, error: error.message };
      }
      
      return { success: true };
    } catch (error: any) {
      // Check for 503 in catch
      if (error.message?.includes('503') || error.status === 503 || error.code === '503') {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
        return { 
          success: false, 
          isCloudflareDown: true,
          error: 'Cloudflare service is temporarily unavailable. Please try again later.' 
        };
      }
      
      if (error.message?.includes('fetch') || error.message?.includes('Network')) {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          continue;
        }
        return { 
          success: false, 
          isNetworkError: true,
          error: 'Network error: Cannot connect to server.' 
        };
      }
      
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        continue;
      }
      return { success: false, error: error.message || 'Connection failed' };
    }
  }
  
  return { success: false, error: 'Connection failed after retries' };
};

/**
 * Check if error is a Cloudflare 503 error
 */
export const isCloudflare503Error = (error: any): boolean => {
  if (!error) return false;
  return (
    error.message?.includes('503') ||
    error.message?.includes('Service Unavailable') ||
    error.status === 503 ||
    error.code === '503' ||
    (error as any).response?.status === 503
  );
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: any): boolean => {
  if (!error) return false;
  return (
    error.message?.includes('fetch') ||
    error.message?.includes('Network') ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('NetworkError') ||
    error.name === 'NetworkError'
  );
};

